import type { Programme, Scholarship, UniversityProfile } from '../types/admissions'

/**
 * Discovery logic, shared by every Discover presentation.
 *
 * Both approved compositions render the same records through the same filters,
 * the same deadline reading and the same derived match. None of this lives in a
 * component, so the two designs cannot drift apart in what they actually say.
 */

/** The platform's demonstration "today". */
export const TODAY = new Date('2026-08-16T12:00:00Z')

export type DeadlineTone = 'danger' | 'warn' | 'brand' | 'neutral'

export interface DeadlineState {
  /** Days from today. Negative means the date has passed. */
  days: number
  label: string
  tone: DeadlineTone
  /** True once the date has passed — the record stays, the action does not. */
  closed: boolean
}

/**
 * Reads the trailing date out of a published label such as
 * "Closes 30 Sep 2026" or "Closed 13 Jan 2027".
 */
export function parseDeadline(label: string): Date | null {
  const match = label.match(/(\d{1,2}\s+\w{3,}\s+\d{4})/)
  if (!match) return null
  const parsed = new Date(`${match[1]} 00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function deadlineState(source: string): DeadlineState {
  const date = source.includes('-') ? new Date(`${source}T00:00:00Z`) : parseDeadline(source)

  if (!date || Number.isNaN(date.getTime())) {
    return { days: Number.POSITIVE_INFINITY, label: 'No date published', tone: 'neutral', closed: false }
  }

  const days = Math.round((date.getTime() - TODAY.getTime()) / (24 * 60 * 60 * 1000))

  if (days < 0) return { days, label: 'Closed', tone: 'neutral', closed: true }
  if (days === 0) return { days, label: 'Closing today', tone: 'danger', closed: false }
  if (days <= 7) return { days, label: `Closing in ${days} days`, tone: 'danger', closed: false }
  if (days <= 30) return { days, label: `Closing in ${days} days`, tone: 'warn', closed: false }
  if (days <= 90) return { days, label: `Closes in ${Math.round(days / 7)} weeks`, tone: 'brand', closed: false }
  return { days, label: 'Open', tone: 'brand', closed: false }
}

/** The soonest live deadline across an institution's programmes. */
export function nextDeadline(programmes: Programme[]): Programme | undefined {
  return [...programmes]
    .filter((programme) => !deadlineState(programme.deadline).closed)
    .sort((a, b) => a.deadline.localeCompare(b.deadline))[0]
}

/* ------------------------------------------------------------------ filters */

export type DeadlineWindow = 'Anytime' | 'This month' | 'Next 3 months' | 'This year'

export interface DiscoverFilters {
  country: string
  institutionType: string
  location: string
  field: string
  level: string
  /** Naira ceiling. At the maximum the filter is off. */
  tuitionMax: number
  scholarshipsOnly: boolean
  internationalOnly: boolean
  deadline: DeadlineWindow
}

export const TUITION_CEILING = 5_000_000

export const defaultFilters: DiscoverFilters = {
  country: 'All',
  institutionType: 'All',
  location: 'All',
  field: 'All',
  level: 'All',
  tuitionMax: TUITION_CEILING,
  scholarshipsOnly: false,
  internationalOnly: false,
  deadline: 'Anytime',
}

function withinWindow(state: DeadlineState, window: DeadlineWindow): boolean {
  if (window === 'Anytime') return true
  if (state.closed) return false
  if (window === 'This month') return state.days <= 31
  if (window === 'Next 3 months') return state.days <= 92
  return state.days <= 365
}

function matchesQuery(haystack: string[], query: string): boolean {
  const term = query.trim().toLowerCase()
  if (term.length < 2) return true
  return haystack.join(' ').toLowerCase().includes(term)
}

export function filterUniversities(
  universities: UniversityProfile[],
  programmes: Programme[],
  filters: DiscoverFilters,
  query: string,
): UniversityProfile[] {
  return universities.filter((university) => {
    const owned = programmes.filter((programme) => programme.universityId === university.id)

    if (filters.country !== 'All' && university.country !== filters.country) return false
    if (filters.institutionType !== 'All' && university.type !== filters.institutionType) return false
    if (filters.location !== 'All' && university.city !== filters.location) return false
    if (filters.internationalOnly && !university.internationalIntake) return false
    if (filters.scholarshipsOnly && university.scholarshipsAvailable === 0) return false
    if (filters.field !== 'All' && !owned.some((programme) => programme.field === filters.field)) {
      return false
    }
    if (filters.level !== 'All' && !owned.some((programme) => programme.level === filters.level)) {
      return false
    }
    if (filters.deadline !== 'Anytime') {
      const open = owned.some((programme) =>
        withinWindow(deadlineState(programme.deadline), filters.deadline),
      )
      if (!open) return false
    }
    return matchesQuery(
      [
        university.name,
        university.shortName,
        university.city,
        university.state,
        university.country,
        ...owned.map((programme) => `${programme.title} ${programme.field}`),
      ],
      query,
    )
  })
}

export function filterProgrammes(
  programmes: Programme[],
  universities: UniversityProfile[],
  filters: DiscoverFilters,
  query: string,
): Programme[] {
  return programmes.filter((programme) => {
    const university = universities.find((item) => item.id === programme.universityId)

    if (filters.country !== 'All' && university?.country !== filters.country) return false
    if (filters.institutionType !== 'All' && university?.type !== filters.institutionType) return false
    if (filters.location !== 'All' && university?.city !== filters.location) return false
    if (filters.internationalOnly && !university?.internationalIntake) return false
    if (filters.level !== 'All' && programme.level !== filters.level) return false
    if (filters.field !== 'All' && programme.field !== filters.field) return false
    if (filters.scholarshipsOnly && programme.scholarshipIds.length === 0) return false
    // Tuition is compared only where it is quoted in naira. Converting another
    // currency would mean inventing a rate, so those programmes pass through
    // rather than being silently dropped.
    if (
      filters.tuitionMax < TUITION_CEILING &&
      programme.currency === 'NGN' &&
      programme.tuitionPerYear > filters.tuitionMax
    ) {
      return false
    }
    if (!withinWindow(deadlineState(programme.deadline), filters.deadline)) return false

    return matchesQuery(
      [programme.title, programme.field, programme.level, university?.name ?? '', university?.country ?? ''],
      query,
    )
  })
}

export function filterScholarships(
  scholarships: Scholarship[],
  filters: DiscoverFilters,
  query: string,
): Scholarship[] {
  return scholarships.filter((scholarship) => {
    if (filters.level !== 'All' && !scholarship.level.includes(filters.level)) return false
    if (!withinWindow(deadlineState(scholarship.deadlineLabel), filters.deadline)) return false
    return matchesQuery(
      [scholarship.name, scholarship.funder, scholarship.coverage, scholarship.level],
      query,
    )
  })
}

/* -------------------------------------------------------------------- match */

export interface MatchResult {
  score: number
  reason: string
}

/**
 * A derived, explainable fit reading — never presented as an assessment of
 * whether the student would be admitted. It is computed only from things the
 * student can see: their own field and level, and published institution facts.
 */
export function matchFor(
  university: UniversityProfile,
  programmes: Programme[],
  context: { field?: string; level?: string },
): MatchResult {
  const owned = programmes.filter((programme) => programme.universityId === university.id)
  const reasons: string[] = []
  let score = 55

  if (context.field && owned.some((programme) => programme.field === context.field)) {
    score += 22
    reasons.push(`teaches ${context.field}`)
  }
  if (context.level && owned.some((programme) => programme.level === context.level)) {
    score += 8
    reasons.push(`offers your level`)
  }
  if (university.scholarshipsAvailable >= 4) {
    score += 7
    reasons.push(`${university.scholarshipsAvailable} funding routes`)
  }
  if (university.accommodationOnCampus) {
    score += 4
    reasons.push('on-campus housing')
  }
  if (university.provenance === 'verified') {
    score += 4
    reasons.push('published by the institution')
  }

  return {
    score: Math.min(96, score),
    reason: reasons.length > 0 ? `Because it ${reasons.join(', ')}.` : 'Ranked by how many students open it.',
  }
}

export type SortMode = 'Recommended' | 'Most popular' | 'Recently added'

export function sortUniversities(
  universities: UniversityProfile[],
  programmes: Programme[],
  mode: SortMode,
  context: { field?: string; level?: string },
): UniversityProfile[] {
  const list = [...universities]
  if (mode === 'Most popular') return list.sort((a, b) => b.students - a.students)
  if (mode === 'Recently added') return list.sort((a, b) => b.founded - a.founded)
  return list.sort(
    (a, b) =>
      matchFor(b, programmes, context).score - matchFor(a, programmes, context).score ||
      b.students - a.students,
  )
}
